import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import {
  AGENTS,
  DATABASE_STEPS,
  DEPLOY_STEPS,
} from '@/components/onboarding-sheet/agents'
import { ONBOARDING_PROGRESS_KEY } from '@/config/local-storage'
import type { AgentId } from '@/components/onboarding-sheet/agents'
import type { ReactNode } from 'react'

export type OnboardingPath = 'agent' | 'deploy' | 'database'
export type OnboardingView = 'cta' | 'agent-select' | 'steps'
export type OnboardingEntrypoint = 'dashboard' | 'help' | 'signup'

type ProgressState = {
  selectedPath: OnboardingPath | null
  selectedAgent: AgentId | null
  completedSteps: number[]
  isDismissed: boolean
}

const DEFAULT_PROGRESS: ProgressState = {
  selectedPath: null,
  selectedAgent: null,
  completedSteps: [],
  isDismissed: false,
}

const readProgress = (): ProgressState => {
  try {
    const val = localStorage.getItem(ONBOARDING_PROGRESS_KEY)
    return val ? (JSON.parse(val) as ProgressState) : DEFAULT_PROGRESS
  } catch {
    return DEFAULT_PROGRESS
  }
}

const writeProgress = (state: ProgressState) => {
  try {
    localStorage.setItem(ONBOARDING_PROGRESS_KEY, JSON.stringify(state))
  } catch {
    // ignore localStorage write failures
  }
}

type OnboardingSheetContextValue = {
  isOpen: boolean
  open: (view?: OnboardingView, entrypoint?: OnboardingEntrypoint) => void
  close: () => void
  dismiss: () => void
  isDismissed: boolean
  view: OnboardingView
  setView: (view: OnboardingView) => void
  selectedPath: OnboardingPath | null
  setSelectedPath: (path: OnboardingPath) => void
  selectedAgent: AgentId | null
  setSelectedAgent: (agentId: AgentId) => void
  completedSteps: number[]
  toggleStep: (stepIndex: number) => void
  completeStep: (stepIndex: number) => void
  totalSteps: number
  resetPath: () => void
  entrypoint: OnboardingEntrypoint | null
}

export const OnboardingSheetContext =
  createContext<OnboardingSheetContextValue | null>(null)

export const useOnboardingSheet = () => {
  const context = useContext(OnboardingSheetContext)
  if (!context) {
    throw new Error(
      'useOnboardingSheet must be used within OnboardingSheetProvider'
    )
  }
  return context
}

export const OnboardingSheetProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [view, setView] = useState<OnboardingView>('cta')
  const [progress, setProgress] = useState<ProgressState>(readProgress)
  const [entrypoint, setEntrypoint] = useState<OnboardingEntrypoint | null>(
    null
  )

  const updateProgress = useCallback((updates: Partial<ProgressState>) => {
    setProgress((prev) => {
      const next = { ...prev, ...updates }
      writeProgress(next)
      return next
    })
  }, [])

  const setSelectedPath = useCallback(
    (path: OnboardingPath) => {
      updateProgress({
        selectedPath: path,
        selectedAgent: null,
        completedSteps: [],
      })
    },
    [updateProgress]
  )

  const setSelectedAgent = useCallback(
    (agentId: AgentId) => {
      updateProgress({ selectedAgent: agentId, completedSteps: [] })
    },
    [updateProgress]
  )

  const toggleStep = useCallback((stepIndex: number) => {
    setProgress((prev) => {
      const steps = prev.completedSteps.includes(stepIndex)
        ? prev.completedSteps.filter((i) => i !== stepIndex)
        : [...prev.completedSteps, stepIndex]
      const next = { ...prev, completedSteps: steps }
      writeProgress(next)
      return next
    })
  }, [])

  const completeStep = useCallback((stepIndex: number) => {
    setProgress((prev) => {
      if (prev.completedSteps.includes(stepIndex)) {
        return prev
      }
      const next = {
        ...prev,
        completedSteps: [...prev.completedSteps, stepIndex],
      }
      writeProgress(next)
      return next
    })
  }, [])

  const resetPath = useCallback(() => {
    updateProgress({
      selectedPath: null,
      selectedAgent: null,
      completedSteps: [],
    })
  }, [updateProgress])

  const open = useCallback(
    (
      initialView?: OnboardingView,
      newEntrypoint: OnboardingEntrypoint = 'signup'
    ) => {
      const saved = readProgress()
      const isAutomaticOpen = newEntrypoint === 'signup'
      if (isAutomaticOpen && saved.isDismissed) {
        return
      }
      if (initialView) {
        setView(initialView)
      } else if (saved.selectedPath === 'agent' && saved.selectedAgent) {
        setView('steps')
      } else if (
        saved.selectedPath === 'deploy' ||
        saved.selectedPath === 'database'
      ) {
        setView('steps')
      } else {
        setView('cta')
      }
      setEntrypoint(newEntrypoint)
      setIsOpen(true)
    },
    []
  )

  const close = useCallback(() => setIsOpen(false), [])

  const dismiss = useCallback(() => {
    updateProgress({ isDismissed: true })
    setIsOpen(false)
  }, [updateProgress])

  const totalSteps = useMemo(() => {
    if (progress.selectedPath === 'deploy') {
      return DEPLOY_STEPS.length
    }
    if (progress.selectedPath === 'database') {
      return DATABASE_STEPS.length
    }
    if (progress.selectedPath === 'agent' && progress.selectedAgent) {
      return (
        AGENTS.find((a) => a.id === progress.selectedAgent)?.steps.length ?? 3
      )
    }
    return 0
  }, [progress.selectedPath, progress.selectedAgent])

  const value = useMemo<OnboardingSheetContextValue>(
    () => ({
      isOpen,
      open,
      close,
      dismiss,
      isDismissed: progress.isDismissed,
      view,
      setView,
      selectedPath: progress.selectedPath,
      setSelectedPath,
      selectedAgent: progress.selectedAgent,
      setSelectedAgent,
      completedSteps: progress.completedSteps,
      toggleStep,
      completeStep,
      totalSteps,
      resetPath,
      entrypoint,
    }),
    [
      isOpen,
      open,
      close,
      dismiss,
      view,
      progress,
      setSelectedPath,
      setSelectedAgent,
      toggleStep,
      completeStep,
      totalSteps,
      resetPath,
      entrypoint,
    ]
  )

  return (
    <OnboardingSheetContext.Provider value={value}>
      {children}
    </OnboardingSheetContext.Provider>
  )
}
